import { useQuery } from '@tanstack/react-query';
import { getRouteApi } from '@tanstack/react-router';
import { Table, Text } from '@gravity-ui/uikit';
import { useTenantsApi } from '../../lib/api/context.js';
import { count, moment, percent, rub, rubCompact } from '../../ui/format.js';
import { MetricStrip } from '../../ui/metric-strip.js';
import { MonthChart } from '../../ui/month-chart.js';
import { Page, Section } from '../../ui/page.js';
import { PeriodPicker } from '../../ui/period-picker.js';
import { periodOf } from '../../ui/period.js';
import { RouterLink } from '../../ui/router-link.js';
import { Empty, ErrorNote, LoadingRows } from '../../ui/states.js';

/**
 * Личная статистика менеджера: клиники, оплаты, процент, премии и переходы
 * по его ссылкам. Чужих клиник и чужих денег здесь нет — сервер отдаёт только
 * то, что закреплено за вошедшим.
 */

const routeApi = getRouteApi('/shell/');

export function HomeScreen() {
  const api = useTenantsApi();
  const search = routeApi.useSearch();
  const navigate = routeApi.useNavigate();
  const period = periodOf(search);

  const stats = useQuery({
    queryKey: ['my-stats', period.from, period.to],
    queryFn: () => api.stats(period),
    placeholderData: (previous) => previous,
  });
  const earnings = useQuery({
    queryKey: ['my-earnings', period.from, period.to],
    queryFn: () => api.earnings(period),
  });

  const data = stats.data;
  const conversion = data === undefined || data.visits === 0 ? null : Math.round((data.registrations / data.visits) * 1000) / 10;

  return (
    <Page
      title="Мои показатели"
      description="Только ваши клиники: оплаты их подписок, ваш процент и премии."
      actions={
        <PeriodPicker
          value={period}
          onChange={(next) => {
            void navigate({ search: { from: next.from, to: next.to } });
          }}
        />
      }
    >
      {stats.isError ? (
        <ErrorNote title="Показатели не загрузились" error={stats.error} onRetry={() => void stats.refetch()} />
      ) : (
        <MetricStrip
          loading={data === undefined}
          metrics={[
            {
              label: 'Мои клиники',
              value: count(data?.tenants.total ?? 0),
              hint: data === undefined ? undefined : `${count(data.tenants.paying)} платят · ${count(data.tenants.trial)} на пробном`,
              href: '/tenants',
            },
            { label: 'Оплаты клиник', value: rub(data?.revenueMinor ?? 0), hint: 'за вычетом возвратов' },
            { label: 'Мой процент', value: rub(data?.commissionMinor ?? 0), hint: data === undefined ? undefined : `ставка ${percent(data.commissionPercent)}` },
            { label: 'Премии', value: rub(data?.bonusesMinor ?? 0) },
            { label: 'К выплате', value: rub(data?.payoutMinor ?? 0), tone: 'positive' },
          ]}
        />
      )}

      <Section title="По месяцам">
        {data === undefined ? (
          <LoadingRows rows={1} height={220} />
        ) : (
          <MonthChart
            title="Оплаты клиник и начисления по месяцам"
            months={data.months.map((row) => row.month)}
            series={[
              { key: 'revenue', label: 'Оплаты клиник', color: 'var(--g-color-base-info-heavy)', values: data.months.map((row) => row.revenueMinor) },
              {
                key: 'payout',
                label: 'К выплате',
                color: 'var(--g-color-base-brand)',
                values: data.months.map((row) => row.commissionMinor + row.bonusesMinor),
              },
            ]}
            format={rub}
            formatAxis={rubCompact}
          />
        )}
      </Section>

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
        <Section title="Ссылки за период">
          {data === undefined ? (
            <LoadingRows rows={3} height={28} />
          ) : (
            <div className="flex flex-col gap-2">
              <Line label="Переходов" value={count(data.visits)} />
              <Line label="Зарегистрировались" value={count(data.registrations)} />
              <Line label="Конверсия" value={conversion === null ? 'нет данных' : percent(conversion)} />
              <Line label="Новых клиник" value={count(data.newTenants)} />
              <RouterLink to="/links">Мои ссылки</RouterLink>
            </div>
          )}
        </Section>
        <Section title="Звонки и письма">
          {data === undefined ? (
            <LoadingRows rows={3} height={28} />
          ) : (
            <div className="flex flex-col gap-2">
              <Line label="Звонков" value={count(data.activity.calls)} />
              <Line label="Писем" value={count(data.activity.emails)} />
              <Line label="Открыл карточек" value={count(data.activity.views)} />
              <Text color="secondary">
                {data.activity.lastLoginAt === null ? 'Первый вход' : `Последний вход ${moment(data.activity.lastLoginAt)}`}
              </Text>
            </div>
          )}
        </Section>
      </div>

      <Section title="Начисления за период" flush>
        {earnings.isError ? (
          <div className="px-5 pb-3">
            <ErrorNote title="Начисления не загрузились" error={earnings.error} onRetry={() => void earnings.refetch()} />
          </div>
        ) : earnings.data === undefined ? (
          <div className="px-5 pb-3">
            <LoadingRows rows={4} />
          </div>
        ) : earnings.data.accruals.length === 0 && earnings.data.bonuses.filter((row) => row.cancelledAt === null).length === 0 ? (
          <div className="px-5">
            <Empty title="За период начислений нет" description="Процент появится, когда клиника оплатит подписку." />
          </div>
        ) : (
          <div>
            <Table
              className="w-full"
              edgePadding
              stickyHorizontalScroll
              data={earnings.data.accruals}
              getRowDescriptor={(row) => ({ id: row.paymentId })}
              columns={[
                { id: 'at', name: 'Оплата', template: (row) => <span className="tabular">{moment(row.settledAt)}</span> },
                { id: 'tenant', name: 'Клиника', template: (row) => <RouterLink to={`/tenants/${row.tenantId}`}>{row.tenantName}</RouterLink> },
                { id: 'kind', name: 'Тип', template: (row) => paymentKind(row.kind) },
                { id: 'amount', name: 'Сумма', align: 'end', template: (row) => <span className="tabular">{rub(row.amountMinor)}</span> },
                { id: 'rate', name: 'Ставка', align: 'end', template: (row) => <span className="tabular">{percent(row.percent)}</span> },
                { id: 'commission', name: 'Мой процент', align: 'end', template: (row) => <span className="tabular">{rub(row.commissionMinor)}</span> },
              ]}
            />
          </div>
        )}
      </Section>
    </Page>
  );
}

function Line({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <Text color="secondary">{label}</Text>
      <Text className="tabular">{value}</Text>
    </div>
  );
}

function paymentKind(kind: 'initial' | 'recurring' | 'manual'): string {
  return kind === 'initial' ? 'Первая оплата' : kind === 'manual' ? 'Вручную' : 'Продление';
}
